import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Casino as CasinoIcon,
  Star as StarIcon,
  Psychology as OlimpicoIcon,
  EmojiEvents as HeroeIcon,
  Pets as DragonIcon,
  Shield as CaballeroIcon,
  Whatshot as DesafianteIcon,
  Security as DefensorIcon,
  AccountBalance as FaraonIcon,
  School as SacerdoteIcon,
  AutoAwesome as FaerieIcon,
  DarkMode as SombraIcon,
  AccountTree as TitanIcon,
  AllInclusive as EternoIcon
} from '@mui/icons-material';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';

const RAZA_ICONS = {
  Olimpico: OlimpicoIcon,
  Heroe: HeroeIcon,
  Dragon: DragonIcon,
  Caballero: CaballeroIcon,
  Desafiante: DesafianteIcon,
  Defensor: DefensorIcon,
  Faraon: FaraonIcon,
  Sacerdote: SacerdoteIcon,
  Faerie: FaerieIcon,
  Sombra: SombraIcon,
  Titan: TitanIcon,
  Eterno: EternoIcon
};

function DecksPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Primero, verificamos si hay usuario antes de inicializar cualquier estado
  useEffect(() => {
    if (!user) {
      console.log('No user found, redirecting to /login');
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // Si no hay usuario, retornamos null inmediatamente
  if (!user) {
    return null;
  }

  // El resto del componente solo se renderiza si hay usuario
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newDeck, setNewDeck] = useState({
    name: '',
    description: '',
    raza: 'Olimpico'
  });
  const [defaultDeckId, setDefaultDeckId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deckToEdit, setDeckToEdit] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    raza: ''
  });
  const [editError, setEditError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchDecks = async () => {
      try {
        console.log('Fetching decks...');
        const res = await api.get('/api/decks');
        if (mounted) {
          setDecks(res.data);
          setError(null);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching decks:', error);
        if (mounted) {
          if (error.response?.status === 401) {
            navigate('/login', { replace: true });
          } else {
            setError(error.response?.data?.error || 'Error al cargar los mazos');
            setLoading(false);
          }
        }
      }
    };

    fetchDecks();

    return () => {
      mounted = false;
    };
  }, [navigate]); // Removemos user de las dependencias ya que ya verificamos arriba

  async function createDeck(e) {
    e.preventDefault();
    
    if (!newDeck.name.trim()) {
      setError('El nombre del mazo es obligatorio');
      return;
    }

    // Verificar si ya existe un mazo con el mismo nombre
    if (decks.some(deck => deck.name.toLowerCase() === newDeck.name.trim().toLowerCase())) {
      setError('Ya existe un mazo con este nombre');
      return;
    }

    try {
      const res = await api.post('/api/decks', newDeck);
      // La respuesta del servidor probablemente devuelve directamente el deck creado
      // No necesitamos verificar res.data.success
      setDecks([...decks, res.data]); // Añadir el nuevo mazo a la lista
      setNewDeck({ name: '', description: '', raza: 'Olimpico' }); // Reset form
      setError(null); // Limpiar cualquier error previo
    } catch (error) {
      console.error('Error creating deck:', error);
      setError(
        error.response?.data?.message || 
        error.response?.data?.error || 
        'Error al crear el mazo. Por favor, inténtalo de nuevo.'
      );
    }
  }

  async function handleDefaultChange(deckId) {
    try {
      await api.put(`/api/decks/${deckId}/default`);
      // Actualizar la lista de mazos para reflejar el cambio
      const res = await api.get('/api/decks');
      setDecks(res.data);
      setError(null);
    } catch (error) {
      console.error('Error setting default deck:', error);
      setError(
        error.response?.data?.message || 
        'Error al establecer el mazo por defecto. Por favor, inténtalo de nuevo.'
      );
    }
  }

  async function handleDeleteDeck(deckId) {
    try {
      await api.delete(`/api/decks/${deckId}`);
      setDecks(decks.filter(deck => deck.id !== deckId));
      setError(null);
      setDeleteDialogOpen(false);
      setDeckToDelete(null);
    } catch (error) {
      console.error('Error deleting deck:', error);
      setError(
        error.response?.data?.message || 
        'Error al eliminar el mazo. Por favor, inténtalo de nuevo.'
      );
    }
  }

  const handleOpenDeleteDialog = (deck) => {
    setDeckToDelete(deck);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeckToDelete(null);
  };

  const handleOpenEditDialog = (deck) => {
    setDeckToEdit(deck);
    setEditForm({
      name: deck.name,
      raza: deck.raza
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setDeckToEdit(null);
    setEditForm({ name: '', raza: '' });
    setEditError(null);
  };

  const handleEditDeck = async () => {
    if (!editForm.name.trim()) {
      setEditError('El nombre del mazo es obligatorio');
      return;
    }

    // Verificar si ya existe un mazo con el mismo nombre (excluyendo el mazo actual)
    if (decks.some(deck => 
      deck.id !== deckToEdit.id && 
      deck.name.toLowerCase() === editForm.name.trim().toLowerCase()
    )) {
      setEditError('Ya existe un mazo con este nombre');
      return;
    }

    try {
      const res = await api.put(`/api/decks/${deckToEdit.id}`, editForm);
      // Actualizar el mazo en el estado local manteniendo todas sus propiedades
      setDecks(decks.map(deck => 
        deck.id === deckToEdit.id 
          ? { ...deck, ...editForm } // Mantener todas las props del mazo y actualizar solo las editadas
          : deck
      ));
      setError(null);
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error updating deck:', error);
      setEditError(
        error.response?.data?.message || 
        'Error al actualizar el mazo. Por favor, inténtalo de nuevo.'
      );
    }
  };

  const DeckCard = ({ deck, isDefault, onSetDefault }) => {
    const RazaIcon = RAZA_ICONS[deck.raza] || CasinoIcon;
    
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.8,
          borderRadius: 2,
          border: '1px solid',
          borderColor: isDefault ? 'primary.main' : 'divider',
          bgcolor: 'background.paper',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
          }
        }}
      >
        <Stack spacing={1.5}>
          {/* Deck Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 32, 
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {isDefault && (
                  <StarIcon 
                    sx={{ 
                      color: 'primary.main',
                      fontSize: '1.5rem'
                    }} 
                  />
                )}
              </Box>
              <Box>
                <Typography 
                  variant="subtitle2" 
                  fontWeight="medium"
                  sx={{ fontSize: '1.15em' }}
                >
                  {deck.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {deck.id}
                </Typography>
                {isDefault && (
                  <Typography 
                    variant="caption" 
                    color="primary"
                    sx={{ 
                      display: 'block',
                      fontWeight: 'medium'
                    }}
                  >
                    Por defecto
                  </Typography>
                )}
              </Box>
            </Box>
            <Chip
              size="small"
              label={deck.raza}
              icon={<RazaIcon sx={{ fontSize: '1rem !important' }} />}
              variant="outlined"
              sx={{ 
                borderRadius: 1,
                height: 28,
                '& .MuiChip-label': { 
                  fontSize: '0.96rem',
                  fontWeight: 'medium'
                }
              }}
            />
          </Box>

          {/* Actions */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: 1
          }}>
            {!isDefault && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onSetDefault(deck.id)}
                startIcon={<StarIcon />}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: '0.8rem'
                }}
              >
                Establecer por defecto
              </Button>
            )}
            <IconButton 
              size="small" 
              color="primary"
              onClick={() => handleOpenEditDialog(deck)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              color="error"
              onClick={() => handleOpenDeleteDialog(deck)}
              disabled={isDefault}
              sx={{
                '&.Mui-disabled': {
                  color: 'action.disabled'
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Stack>
      </Paper>
    );
  };

  const sortDecks = (decks) => {
    return [...decks].sort((a, b) => {
      // Primero el mazo por defecto
      if (a.is_default) return -1;
      if (b.is_default) return 1;
      // Luego ordenar por ID de forma descendente
      return b.id - a.id;
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        p: 3,
        mt: '64px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Typography>Cargando mazos...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        p: 3,
        mt: '64px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}>
        <Typography color="error" gutterBottom>
          Error: {error}
        </Typography>
        <Button variant="contained" onClick={() => {
          setLoading(true);
          setError(null);
          fetchDecks();
        }}>
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ 
        p: { xs: 1, sm: 2 },
        mt: '64px',
        mb: '56px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: '800px',
        mx: 'auto'
      }}>
        <Stack spacing={2} sx={{ width: '100%' }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}>
            <Typography 
              variant="h5" 
              fontWeight="bold"
              color="primary"
            >
              Mis Mazos
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {/* Create Deck Card */}
          <Paper
            elevation={0}
            sx={{ 
              p: 1.8,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <Stack spacing={1.8}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Nuevo Mazo
              </Typography>

              <Stack spacing={2} component="form" onSubmit={createDeck}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nombre del Mazo"
                    value={newDeck.name}
                    onChange={(e) => setNewDeck({ ...newDeck, name: e.target.value })}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5
                      }
                    }}
                  />

                  <FormControl sx={{ minWidth: 200 }}>
                    <Select
                      size="small"
                      value={newDeck.raza}
                      onChange={(e) => setNewDeck({ ...newDeck, raza: e.target.value })}
                      sx={{
                        borderRadius: 1.5,
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }
                      }}
                    >
                      {['Olimpico', 'Heroe', 'Dragon', 'Caballero', 'Desafiante', 'Defensor', 'Faraon', 'Sacerdote', 'Faerie', 'Sombra', 'Titan', 'Eterno'].map((raza) => {
                        const RazaIcon = RAZA_ICONS[raza] || CasinoIcon;
                        return (
                          <MenuItem key={raza} value={raza}>
                            <RazaIcon fontSize="small" sx={{ color: 'primary.main' }} />
                            {raza}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 'medium'
                  }}
                >
                  Crear Mazo
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Decks List */}
          <Paper
            elevation={0}
            sx={{ 
              p: 1.8,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <Stack spacing={1.8}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Mis Mazos
              </Typography>

              <Stack spacing={1.5}>
                {decks.length > 0 ? (
                  sortDecks(decks).map((deck) => (
                    <DeckCard
                      key={deck.id}
                      deck={deck}
                      isDefault={deck.is_default}
                      onSetDefault={handleDefaultChange}
                    />
                  ))
                ) : (
                  <Box sx={{ 
                    py: 4, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <CasinoIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    <Typography color="text.secondary">
                      No hay mazos creados
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: '100%',
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          fontWeight: 'bold'
        }}>
          Confirmar eliminación
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography>
            ¿Estás seguro de que quieres eliminar el mazo "{deckToDelete?.name}"?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.9rem'
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleDeleteDeck(deckToDelete?.id)}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.9rem'
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: '100%',
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          fontWeight: 'bold'
        }}>
          Editar Mazo
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          {editError && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: 2
              }}
            >
              {editError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Nombre del Mazo"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5
                }
              }}
            />
            <FormControl fullWidth>
              <Select
                size="small"
                value={editForm.raza}
                onChange={(e) => setEditForm({ ...editForm, raza: e.target.value })}
                sx={{
                  borderRadius: 1.5,
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }
                }}
              >
                {['Olimpico', 'Heroe', 'Dragon', 'Caballero', 'Desafiante', 'Defensor', 'Faraon', 'Sacerdote', 'Faerie', 'Sombra', 'Titan', 'Eterno'].map((raza) => {
                  const RazaIcon = RAZA_ICONS[raza] || CasinoIcon;
                  return (
                    <MenuItem key={raza} value={raza}>
                      <RazaIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      {raza}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.9rem'
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEditDeck}
            variant="contained"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.9rem'
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DecksPage;